import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import './CondizioniHtmlEditor.css';

type ViewMode = 'wysiwyg' | 'html';

export interface CondizioniHtmlEditorProps {
  /** HTML iniziale e valore controllato. */
  value: string;
  /** Notifica cambiamenti (HTML aggiornato). */
  onChange: (html: string) => void;
  /** Disabilita editing. */
  disabled?: boolean;
  /** Altezza minima editor (default 360 px). */
  minHeight?: number;
}

/**
 * Editor HTML WYSIWYG (TipTap) per le condizioni generali documenti.
 * Usato in:
 *  - Impostazioni sedi → editor condizioni di default per tipo documento
 *  - Dettaglio preventivo → editor condizioni freezate sul singolo documento
 */
export default function CondizioniHtmlEditor({
  value,
  onChange,
  disabled = false,
  minHeight = 360,
}: CondizioniHtmlEditorProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('wysiwyg');
  const [htmlSource, setHtmlSource] = useState(value);

  // True mentre stiamo applicando un valore esterno (caricamento/ricarica).
  // Serve a non propagare onChange per le transazioni di normalizzazione che
  // alcune estensioni TipTap (es. autolink di Link) generano in risposta a
  // setContent: senza questo guard la pagina risulterebbe "modificata" al
  // semplice caricamento, anche senza alcun intervento dell'utente.
  const applyingExternalRef = useRef(false);

  const editor = useEditor({
    extensions: [
      // StarterKit v3 include già Underline e Link: li disabilitiamo per
      // poterli registrare con le nostre opzioni dedicate.
      StarterKit.configure({ underline: false, link: false }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      setHtmlSource(html);
      if (applyingExternalRef.current) return;
      onChange(html);
    },
  });

  // Re-sync quando value cambia da fuori (es. ricarica server)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) {
      applyingExternalRef.current = true;
      editor.commands.setContent(value || '', { emitUpdate: false });
      setHtmlSource(value || '');
      applyingExternalRef.current = false;
    }
  }, [value, editor]);

  // Aggiorna stato editable
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  const switchToHtml = () => {
    if (!editor) return;
    setHtmlSource(editor.getHTML());
    setViewMode('html');
  };

  const switchToWysiwyg = () => {
    if (!editor) return;
    editor.commands.setContent(htmlSource || '', { emitUpdate: false });
    setViewMode('wysiwyg');
  };

  return (
    <div className="cond-html-editor">
      <div className="cond-html-editor__viewswitch">
        <button
          type="button"
          className={`bop-btn ${viewMode === 'wysiwyg' ? 'bop-btn-primary' : 'bop-btn-ghost'}`}
          onClick={switchToWysiwyg}
          disabled={disabled}
        >
          Editor visuale
        </button>
        <button
          type="button"
          className={`bop-btn ${viewMode === 'html' ? 'bop-btn-primary' : 'bop-btn-ghost'}`}
          onClick={switchToHtml}
          disabled={disabled}
        >
          HTML sorgente
        </button>
      </div>

      {viewMode === 'wysiwyg' && (
        <>
          <Toolbar editor={editor} disabled={disabled} />
          <div
            className="cond-html-editor__content"
            style={{ minHeight }}
          >
            <EditorContent editor={editor} />
          </div>
        </>
      )}

      {viewMode === 'html' && (
        <textarea
          className="bop-input cond-html-editor__source"
          value={htmlSource}
          onChange={e => {
            setHtmlSource(e.target.value);
            onChange(e.target.value);
          }}
          spellCheck={false}
          rows={15}
          disabled={disabled}
          style={{ minHeight }}
        />
      )}
    </div>
  );
}

// ── Toolbar ─────────────────────────────────────────────────────────────────
function Toolbar({
  editor,
  disabled,
}: {
  editor: Editor | null;
  disabled: boolean;
}) {
  if (!editor) return null;

  const setLink = () => {
    const url = window.prompt('URL del link (lascia vuoto per rimuovere):', editor.getAttributes('link').href || '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const btn = (active: boolean, btnDisabled: boolean, onClick: () => void, label: string, title?: string) => (
    <button
      type="button"
      className={`cond-html-editor__tb-btn ${active ? 'is-active' : ''}`}
      disabled={disabled || btnDisabled}
      onClick={onClick}
      title={title || label}
    >
      {label}
    </button>
  );

  return (
    <div className="cond-html-editor__toolbar">
      {btn(editor.isActive('bold'), !editor.can().chain().focus().toggleBold().run(),
        () => editor.chain().focus().toggleBold().run(), 'B', 'Grassetto')}
      {btn(editor.isActive('italic'), !editor.can().chain().focus().toggleItalic().run(),
        () => editor.chain().focus().toggleItalic().run(), 'I', 'Corsivo')}
      {btn(editor.isActive('underline'), !editor.can().chain().focus().toggleUnderline().run(),
        () => editor.chain().focus().toggleUnderline().run(), 'U', 'Sottolineato')}
      {btn(editor.isActive('strike'), !editor.can().chain().focus().toggleStrike().run(),
        () => editor.chain().focus().toggleStrike().run(), 'S', 'Barrato')}

      <span className="cond-html-editor__tb-sep" />

      {btn(editor.isActive('heading', { level: 1 }), false,
        () => editor.chain().focus().toggleHeading({ level: 1 }).run(), 'H1')}
      {btn(editor.isActive('heading', { level: 2 }), false,
        () => editor.chain().focus().toggleHeading({ level: 2 }).run(), 'H2')}
      {btn(editor.isActive('heading', { level: 3 }), false,
        () => editor.chain().focus().toggleHeading({ level: 3 }).run(), 'H3')}
      {btn(editor.isActive('paragraph'), false,
        () => editor.chain().focus().setParagraph().run(), 'P', 'Paragrafo')}

      <span className="cond-html-editor__tb-sep" />

      {btn(editor.isActive('bulletList'), false,
        () => editor.chain().focus().toggleBulletList().run(), '• Lista', 'Elenco puntato')}
      {btn(editor.isActive('orderedList'), false,
        () => editor.chain().focus().toggleOrderedList().run(), '1. Lista', 'Elenco numerato')}
      {btn(editor.isActive('blockquote'), false,
        () => editor.chain().focus().toggleBlockquote().run(), '❝', 'Citazione')}

      <span className="cond-html-editor__tb-sep" />

      {btn(editor.isActive({ textAlign: 'left' }), false,
        () => editor.chain().focus().setTextAlign('left').run(), '⇤', 'Allinea a sinistra')}
      {btn(editor.isActive({ textAlign: 'center' }), false,
        () => editor.chain().focus().setTextAlign('center').run(), '↔', 'Centra')}
      {btn(editor.isActive({ textAlign: 'right' }), false,
        () => editor.chain().focus().setTextAlign('right').run(), '⇥', 'Allinea a destra')}
      {btn(editor.isActive({ textAlign: 'justify' }), false,
        () => editor.chain().focus().setTextAlign('justify').run(), '☰', 'Giustifica')}

      <span className="cond-html-editor__tb-sep" />

      {btn(editor.isActive('link'), false, setLink, '🔗', 'Link')}
      {btn(false, false, () => editor.chain().focus().setHorizontalRule().run(), '—', 'Linea separatrice')}

      <span className="cond-html-editor__tb-sep" />

      {btn(false, !editor.can().chain().focus().undo().run(),
        () => editor.chain().focus().undo().run(), '↶', 'Annulla')}
      {btn(false, !editor.can().chain().focus().redo().run(),
        () => editor.chain().focus().redo().run(), '↷', 'Ripeti')}
    </div>
  );
}
